import { PERMISSIONS, PermissionTuple } from '@/shared/permissions';

import { useCASL } from './useCASL';
import { usePlanUsageLimits } from './usePlanUsageLimits';

export type WorkspaceCreatableResource =
    'folders' | 'investments' | 'memos' | 'pipelines' | 'dashboards' | 'lists' | 'teams';

export const workspaceResourceCreatePermissions: Record<WorkspaceCreatableResource, PermissionTuple> = {
    folders: PERMISSIONS.FOLDER.CREATE,
    investments: PERMISSIONS.INVESTMENT.CREATE,
    memos: PERMISSIONS.MEMO.CREATE,
    pipelines: PERMISSIONS.PIPELINE.CREATE,
    dashboards: PERMISSIONS.DASHBOARD.CREATE,
    lists: PERMISSIONS.LIST.CREATE,
    teams: PERMISSIONS.TEAM.CREATE,
};

export const getWorkspaceResourceCreatePermission = (resource: WorkspaceCreatableResource) =>
    workspaceResourceCreatePermissions[resource];

export const useWorkspaceResourceCreateAccess = (resource: WorkspaceCreatableResource, countOverride?: number) => {
    const { accessState } = useCASL();
    const { getUsageState } = usePlanUsageLimits();
    const permissionAccess = accessState(getWorkspaceResourceCreatePermission(resource));
    const reportedUsageState = getUsageState(resource);
    const usageState =
        countOverride === undefined
            ? reportedUsageState
            : getUsageState(resource, Math.max(reportedUsageState.count, countOverride));
    const limitReached = usageState.reached;
    const disabled = permissionAccess.disabled || limitReached;

    return {
        ...permissionAccess,
        allowed: permissionAccess.allowed && !limitReached,
        canInteract: permissionAccess.canInteract && !limitReached,
        canMutate: permissionAccess.canMutate && !limitReached,
        disabled,
        disabledReason: permissionAccess.disabled ? 'Insufficient workspace permissions' : usageState.reason,
        limitReached,
        readOnly: permissionAccess.readOnly || limitReached,
        usageState,
    };
};
